import { isValidCpf, isValidTaxId } from "@/shared/lib/documents"
import { isValidPhone } from "@/shared/lib/phone"
import { isCompletePostalCode } from "@/shared/lib/postalCode"
import type { AccreditationFormValues } from "@/features/accreditation/formModel"

/* ─────────────────────────────────────────────────────────────────────────────
 * Conferencia da ficha antes do envio.
 *
 * Roda inteira, e nao para no primeiro erro: quem preencheu quarenta campos
 * merece ver de uma vez tudo o que falta, e nao descobrir um problema por
 * tentativa de envio.
 *
 * O que e obrigatorio segue a ficha em papel, com duas excecoes deliberadas:
 *
 *   - o bloco 7 (dados bancarios) e inteiro opcional. O credenciamento e
 *     analisado antes de existir repasse, e exigir conta e chave PIX de quem
 *     ainda nem sabe se sera aceito e pedir dado sensivel cedo demais;
 *   - o bloco 8 (documentacao) e uma conferencia do que a pessoa ja tem em
 *     maos. Os arquivos nao sobem por aqui — sao pedidos na resposta.
 * ────────────────────────────────────────────────────────────────────────── */

export type AccreditationErrors = Partial<Record<keyof AccreditationFormValues, string>>

const REQUIRED = "Campo obrigatório."

/** Simples de proposito: recusa o que claramente nao e endereco, e nada mais. */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())
}

export function validate(values: AccreditationFormValues): AccreditationErrors {
  const errors: AccreditationErrors = {}

  /* 1. Tipo de credenciamento */
  if (!values.kind) errors.kind = "Selecione o tipo de credenciamento."

  /* 2. Dados do estabelecimento / profissional */
  if (!values.legalName.trim()) errors.legalName = REQUIRED

  if (!values.taxId.trim()) errors.taxId = REQUIRED
  else if (!isValidTaxId(values.taxId)) errors.taxId = "Informe um CNPJ ou CPF válido."

  if (!values.crmv.trim()) errors.crmv = REQUIRED
  if (!values.address.trim()) errors.address = REQUIRED
  if (!values.district.trim()) errors.district = REQUIRED
  if (!values.city.trim()) errors.city = REQUIRED
  if (!values.state) errors.state = "Selecione a UF."

  if (!values.postalCode.trim()) errors.postalCode = REQUIRED
  else if (!isCompletePostalCode(values.postalCode)) errors.postalCode = "CEP incompleto."

  if (!values.phone.trim()) errors.phone = REQUIRED
  else if (!isValidPhone(values.phone)) errors.phone = "Telefone incompleto."

  if (values.whatsapp.trim() && !isValidPhone(values.whatsapp)) errors.whatsapp = "WhatsApp incompleto."

  if (!values.email.trim()) errors.email = REQUIRED
  else if (!isValidEmail(values.email)) errors.email = "Informe um e-mail válido."

  /* 3. Responsavel tecnico */
  if (!values.managerName.trim()) errors.managerName = REQUIRED
  if (!values.managerCrmv.trim()) errors.managerCrmv = REQUIRED

  if (!values.managerCpf.trim()) errors.managerCpf = REQUIRED
  else if (!isValidCpf(values.managerCpf)) errors.managerCpf = "Informe um CPF válido."

  if (!values.managerPhone.trim()) errors.managerPhone = REQUIRED
  else if (!isValidPhone(values.managerPhone)) errors.managerPhone = "Telefone incompleto."

  if (!values.managerEmail.trim()) errors.managerEmail = REQUIRED
  else if (!isValidEmail(values.managerEmail)) errors.managerEmail = "Informe um e-mail válido."

  /* 4. Servicos disponiveis */
  if (values.services.length === 0 && !values.otherServicesChecked) {
    errors.services = "Selecione ao menos um serviço."
  }

  if (values.otherServicesChecked && !values.otherServices.trim()) {
    errors.otherServices = "Descreva os outros serviços."
  }

  /* 5. Horario de funcionamento */
  if (!values.openingHours.trim()) errors.openingHours = REQUIRED
  if (!values.open24h) errors.open24h = "Informe se há atendimento 24 horas."

  /* 7. Dados bancarios — opcionais, mas conferidos quando preenchidos */
  if (values.bankHolderTaxId.trim() && !isValidTaxId(values.bankHolderTaxId)) {
    errors.bankHolderTaxId = "Informe um CPF ou CNPJ válido."
  }

  /* 8. Documentacao */
  if (values.otherDocumentsChecked && !values.otherDocuments.trim()) {
    errors.otherDocuments = "Descreva os outros documentos."
  }

  /* 9. Declaracao */
  if (!values.declarationPlace.trim()) errors.declarationPlace = REQUIRED
  if (!values.declarationAccepted) errors.declarationAccepted = "É necessário aceitar a declaração."

  return errors
}

/** Ordem de leitura da ficha — define para qual erro a tela rola primeiro. */
export const FIELD_ORDER: ReadonlyArray<keyof AccreditationFormValues> = [
  "kind",
  "legalName",
  "taxId",
  "crmv",
  "address",
  "district",
  "city",
  "state",
  "postalCode",
  "phone",
  "whatsapp",
  "email",
  "managerName",
  "managerCrmv",
  "managerCpf",
  "managerPhone",
  "managerEmail",
  "services",
  "otherServices",
  "openingHours",
  "open24h",
  "bankHolderTaxId",
  "otherDocuments",
  "declarationPlace",
  "declarationAccepted",
]

export function firstErrorField(errors: AccreditationErrors): keyof AccreditationFormValues | null {
  return FIELD_ORDER.find((field) => errors[field]) ?? null
}
